import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/movies";
import { getCategoryName, getFeaturedMovies, formatViews } from "@/utils/movie";

export default function HeroCarousel() {
  const featured = getFeaturedMovies();
  const slides = featured.map((movie, index) => ({
    movie,
    image: heroSlides[index % heroSlides.length],
  }));
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const goPrev = () => setActive((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setActive((prev) => (prev + 1) % slides.length);

  return (
    <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
      <div className="relative w-full overflow-hidden rounded-lg border border-background-200/70 h-[420px] md:h-[560px]">
        {slides.map((slide, index) => (
          <div
            key={slide.movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.movie.title} 焦点海报`}
              title={`${slide.movie.title} - 三级影片`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 flex h-full flex-col justify-end p-6 md:p-10 lg:p-14">
          <div className="max-w-2xl fade-in-up" key={active}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-500 mb-4">
              <i className="ri-fire-line" />
              本周热门推荐
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-3">
              {slides[active].movie.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600 mb-3">
              <span>{slides[active].movie.year}</span>
              <span className="text-foreground-400">·</span>
              <span>{slides[active].movie.region}</span>
              <span className="text-foreground-400">·</span>
              <span>{getCategoryName(slides[active].movie.categoryId)}</span>
              <span className="text-foreground-400">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500" />
                {slides[active].movie.rating.toFixed(1)}
              </span>
              <span className="text-foreground-400">·</span>
              <span className="flex items-center gap-1">
                <i className="ri-play-circle-line" />
                {formatViews(slides[active].movie.views)} 次播放
              </span>
            </div>
            <p className="hidden sm:block text-sm md:text-base leading-relaxed text-foreground-600 line-clamp-2 mb-6">
              {slides[active].movie.synopsis}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[active].movie.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-base" />
                查看详情
              </Link>
              <Link
                to={`/category/${slides[active].movie.categoryId}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-300/60 bg-background-100/60 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                同分类影片
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一部"
          onClick={goPrev}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur-sm text-foreground-900 hover:bg-accent-500 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          aria-label="下一部"
          onClick={goNext}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur-sm text-foreground-900 hover:bg-accent-500 hover:text-foreground-950 transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 z-20 flex items-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.movie.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 部`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-8 bg-accent-500" : "w-4 bg-foreground-400/50 hover:bg-foreground-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}